import React, { useState, useEffect, useCallback } from 'react';
import axios, { API, SUPPORT_EMAIL } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import CreateGoalDialog from '../components/CreateGoalDialog';
import DailyTaskCard from '../components/DailyTaskCard';
import GoalCard from '../components/GoalCard';
import ReportsTab from '../components/ReportsTab';
import ArchivedGoalsTab from '../components/ArchivedGoalsTab';
import FeedbackButton from '../components/FeedbackButton';
import AdminFeedbackTab from '../components/AdminFeedbackTab';
import AIMonitorTab from '../components/AIMonitorTab';
import AdminUsersTab from '../components/AdminUsersTab';
import StartHereDialog from '../components/StartHereDialog';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Target, Clock, CheckCircle, Circle, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logout, user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsResponse, tasksResponse] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tasks/today`),
      ]);
      setDashboardStats(statsResponse.data);
      setTodayTasks(tasksResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        return;
      }
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-justb-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your goals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-justb-primary">
                  Just B. Goal Tracker
                </h1>
                <StartHereDialog autoOpen={!user?.has_seen_onboarding} />
              </div>
              <p className="text-gray-600 text-sm sm:text-base">Transform your aspirations into achievements</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <CreateGoalDialog onGoalCreated={fetchDashboardData} />
              <Button variant="outline" onClick={handleLogout} className="text-gray-600 hover:text-gray-800 w-full sm:w-auto min-h-[48px]">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <Card className="bg-white border-2 border-justb-primary/20 hover:border-justb-primary/40 transition-all duration-300">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-justb-primary text-xs sm:text-sm font-medium">Active Goals</p>
                    <p className="text-xl sm:text-3xl font-bold text-gray-900">{dashboardStats.active_goals}</p>
                  </div>
                  <Target className="h-6 w-6 sm:h-8 sm:w-8 text-justb-primary" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-justb-secondary/20 hover:border-justb-secondary/40 transition-all duration-300">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-justb-secondary text-xs sm:text-sm font-medium">Completed Goals</p>
                    <p className="text-xl sm:text-3xl font-bold text-gray-900">{dashboardStats.completed_goals}</p>
                  </div>
                  <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-justb-secondary" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-slate-500/20 hover:border-slate-500/40 transition-all duration-300 col-span-2 md:col-span-1">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-600 text-xs sm:text-sm font-medium">Today's Progress</p>
                    <p className="text-xl sm:text-3xl font-bold text-gray-900">{dashboardStats.today_tasks_completed}/{dashboardStats.today_tasks_total}</p>
                  </div>
                  <Circle className="h-6 w-6 sm:h-8 sm:w-8 text-slate-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-2 border-amber-500/20 hover:border-amber-500/40 transition-all duration-300 col-span-2 md:col-span-1">
              <CardContent className="p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-600 text-xs sm:text-sm font-medium">Total Time Spent</p>
                    <p className="text-xl sm:text-3xl font-bold text-gray-900">{dashboardStats.total_time_all_goals}<span className="text-sm sm:text-lg">m</span></p>
                  </div>
                  <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs defaultValue="today" className="space-y-6">
          <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-4 sm:grid-cols-7 max-w-5xl' : 'grid-cols-4 max-w-2xl'}`}>
            <TabsTrigger value="today">Today's Tasks</TabsTrigger>
            <TabsTrigger value="goals">Active Goals</TabsTrigger>
            <TabsTrigger value="archived">Archived Goals</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            {isAdmin && <TabsTrigger value="feedback">Feedback</TabsTrigger>}
            {isAdmin && <TabsTrigger value="ai-monitor">AI Monitor</TabsTrigger>}
            {isAdmin && <TabsTrigger value="users">Users</TabsTrigger>}
          </TabsList>

          <TabsContent value="today" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-justb-primary">
                  <Calendar className="mr-2 h-5 w-5" />
                  Today's Focus
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayTasks.length > 0 ? (
                  <div className="space-y-2">
                    {todayTasks.map((task) => (
                      <DailyTaskCard key={task.id} task={task} onTaskUpdate={fetchDashboardData} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Circle className="mx-auto h-12 w-12 mb-4 opacity-50 text-gray-400" />
                    <p className="text-gray-900">No tasks scheduled for today</p>
                    <p className="text-sm text-gray-600">Create a new goal to get started!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="goals" className="space-y-6">
            {dashboardStats?.goals?.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {dashboardStats.goals.map((goal) => (
                  <GoalCard key={goal.id} goal={goal} onGoalUpdate={fetchDashboardData} />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Target className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">No Goals Yet</h3>
                  <p className="text-gray-600 mb-6">Start your journey by creating your first life goal</p>
                  <CreateGoalDialog onGoalCreated={fetchDashboardData} />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="archived" className="space-y-6">
            <ArchivedGoalsTab onGoalUpdate={fetchDashboardData} />
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <ReportsTab />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="feedback" className="space-y-6">
              <AdminFeedbackTab />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="ai-monitor" className="space-y-6">
              <AIMonitorTab />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="users" className="space-y-6">
              <AdminUsersTab />
            </TabsContent>
          )}
        </Tabs>

        <footer className="mt-12 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Need help or have a question? Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Just%20B%20-%20Support`} className="text-justb-secondary hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </footer>
      </div>
      <FeedbackButton />
    </div>
  );
};

export default Dashboard;
